import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { usePagination, useTable } from 'react-table';

import { TableProps } from '../components/Table.type';
import { toCamelCase } from '../utils/stringFormatter';
import Info from './Info';
import TablePagination from './TablePagination';

const Table = (props: TableProps) => {
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		gotoPage,
		pageCount,
		rows,
		page,
		canPreviousPage,
		canNextPage,
		pageOptions,
		nextPage,
		previousPage,
		setHiddenColumns,
		state: { pageIndex },
	} = useTable(
		{
			columns: props.columns,
			data: props.data,
			initialState: {
				pageIndex: 0,
				pageSize: props.pageSize,
			},
			updateData: props.updateData,
			addProductCount: props.addProductCount,
			onKeyPress: props.onKeyPress,
			isSuccess: props.isSuccess,
			counts: props.counts,
			categoryName: props.categoryName,
			idScheduler: props.idScheduler,
		},
		usePagination,
	);

	React.useEffect(() => {
		if (!props.isSwitchProductCode) {
			const hiddenColumns = props.columns
				.filter((item) => item.accessor === 'productCode')
				.map((item) => item.accessor);
			setHiddenColumns(hiddenColumns);
		} else {
			setHiddenColumns([]);
		}
	}, [props.columns, props.isSwitchProductCode, setHiddenColumns]);

	return (
		<div>
			{props.type !== 'outletcrew' ? (
				page && page.length > 0 ? (
					<div className="overflow-auto">
						<table {...getTableProps()} className="w-full">
							<thead>
								{headerGroups.map((headerGroup, index) => (
									<tr key={index} {...headerGroup.getHeaderGroupProps()}>
										{headerGroup.headers.map((column, index2) => (
											<th
												key={index2}
												{...column.getHeaderProps()}
												className={
													column.render('Header') === 'Aksi'
														? 'laptop:text-large tablet:text-large mobile:text-reguler text-gray-gray700 font-interMedium text-center p-3'
														: 'laptop:text-large tablet:text-large mobile:text-reguler text-gray-gray700 font-interMedium text-left p-3'
												}
											>
												{column.render('Header')}
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody {...getTableBodyProps()}>
								{page.map((row, index) => {
									prepareRow(row);
									const isOdd = ((index + 1) & 1) === 1;
									return (
										<tr
											className={
												isOdd
													? 'bg-gray-gray100 hover:bg-gray-gray200'
													: 'bg-white hover:bg-gray-gray200'
											}
											key={index}
											{...row.getRowProps()}
										>
											{row.cells.map((cell, index2) => {
												return (
													<td
														key={index2}
														{...cell.getCellProps()}
														className="laptop:text-large tablet:text-large mobile:text-regular text-large text-gray-gray700 font-interRegular text-left pl-4 laptop:py-8 tablet:py-8 mobile:py-5"
													>
														{cell.render('Cell')}
													</td>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				) : (
					<div className="border border-gray-gray300 rounded-xl flex justify-center py-8">
						<div className="text-extraLarge2 text-gray-gray700 text-center font-interMedium">
							{props.isFilter
								? 'Tidak ada data ditemukan'
								: 'Hasil pencarian tidak ditemukan'}
						</div>
					</div>
				)
			) : (
				<div
					id={toCamelCase(props.categoryName)}
					className="tablet:max-h-144 mobile:max-h-74 overflow-auto mb-3"
				>
					<InfiniteScroll
						scrollThreshold={'100px'}
						initialScrollY={0}
						dataLength={rows.length}
						next={props.fetchPagination}
						hasMore={
							rows.length >= 10 &&
							rows.length < props.countProduct &&
							!props.isSwitchEmptyProduct
						}
						loader={
							<div className="flex justify-center items-center">
								<img
									src="/images/spinnerGrey.gif"
									className="tablet:w-5 tablet:h-5 mobile:w-4 mobile:h-4"
								/>
								<div className="tablet:text-large mobile:text-regular text-gray-gray500 font-interRegular ml-1">
									Memuat
								</div>
							</div>
						}
						endMessage={
							rows.length >= 10 && (
								<Info
									variant="info"
									text="Ini adalah batas terakhir kategori ini"
								/>
							)
						}
						scrollableTarget={toCamelCase(props.categoryName)}
					>
						<table {...getTableProps()} className="w-full">
							<thead>
								{headerGroups.map((headerGroup, index) => (
									<tr key={index} {...headerGroup.getHeaderGroupProps()}>
										{headerGroup.headers.map((column, index2) => (
											<th
												key={index2}
												{...column.getHeaderProps({
													style: {
														minWidth: column.minWidth,
														width: column.width,
													},
												})}
												className="laptop:text-large tablet:text-large mobile:text-regular text-gray-gray700 font-interMedium text-left p-3"
											>
												{column.render('Header')}
											</th>
										))}
									</tr>
								))}
							</thead>
							<tbody {...getTableBodyProps()}>
								{rows.map((row, index) => {
									prepareRow(row);
									const isOdd = ((index + 1) & 1) === 1;
									return (
										<tr
											className={
												isOdd
													? 'bg-gray-gray100 hover:bg-gray-gray200'
													: 'bg-white hover:bg-gray-gray200'
											}
											key={index}
											{...row.getRowProps()}
										>
											{row.cells.map((cell, index2) => {
												return (
													<td
														key={index2}
														className="laptop:text-large tablet:text-large mobile:text-regular text-gray-gray700 font-interRegular text-left pl-4 laptop:py-4 tablet:py-3 mobile:py-2"
													>
														{cell.render('Cell')}
													</td>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						</table>
					</InfiniteScroll>
				</div>
			)}

			{props.type !== 'outletcrew' && (
				<TablePagination
					pageIndex={pageIndex}
					pageOptions={pageOptions}
					pageCount={pageCount}
					previousPage={previousPage}
					canPreviousPage={canPreviousPage}
					nextPage={nextPage}
					canNextPage={canNextPage}
					gotoPage={gotoPage}
					rows={rows}
					page={page}
					pageSize={props.pageSize}
				/>
			)}
		</div>
	);
};

export default Table;
